/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, {
	useCallback,
	useEffect,
	useState,
	useRef,
	type KeyboardEvent,
	type ChangeEvent,
} from 'react';
import { css, jsx } from '@compiled/react';
import { VIEW_NAME_MAX_LENGTH } from '@jira/polaris__component-navigation-store/src/container.constants.tsx';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import AkTextField from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import { OutsideClickAlerter } from '@atlassian/outside-click-alerter/src/OutsideClickAlerter.tsx';
import { fg } from '@atlassian/jira-feature-gating';

type EditableTitleProps = {
	value: string;
	label: string;
	elemBefore: React.ReactNode;
	onChanged: (arg1: string) => void;
	onCancel: (arg1?: string) => void;
};

export const EditableTitle = ({
	value,
	elemBefore,
	onChanged,
	onCancel,
	label,
}: EditableTitleProps): JSX.Element => {
	const textRef = useRef<HTMLInputElement | null>(null);
	const [currentValue, setCurrentValue] = useState(value);
	const triggerRef = useRef(false);
	const hasConfirmIntentRef = useRef(false);

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		triggerRef.current = false;
		hasConfirmIntentRef.current = false;
		// oxlint-disable-next-line react/react-compiler
		setCurrentValue(value);
		// focus the textfield async - there seems to be no reliable alternative to that
		const requestId = setTimeout(() => textRef.current && textRef.current.focus(), 100);
		return () => clearTimeout(requestId);
	}, [value]);

	const onTriggerCancel = useCallback(() => {
		if (!triggerRef.current) {
			triggerRef.current = true;
			setCurrentValue(value);
			onCancel();
		}
	}, [value, onCancel]);

	const onTriggerConfirm = useCallback(() => {
		const finalValue = currentValue.trim();
		if (!triggerRef.current) {
			triggerRef.current = true;
			onChanged(finalValue);
		} else {
			onCancel(finalValue);
		}
	}, [currentValue, onCancel, onChanged]);

	const onKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			const { key } = event;
			if (key === 'Escape' || key === 'Esc') {
				onTriggerCancel();
			}
			if (key === 'Enter') {
				if (fg('pol-23465-section-id-focus-fix')) {
					hasConfirmIntentRef.current = true;
				}
				onTriggerConfirm();
			}
			if (key === 'Tab' && fg('pol-23465-section-id-focus-fix')) {
				hasConfirmIntentRef.current = true;
				onTriggerConfirm();
			}
		},
		[onTriggerCancel, onTriggerConfirm],
	);

	const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setCurrentValue(e.target.value || '');
	}, []);

	const onBlurWithFocus = useCallback(() => {
		if (triggerRef.current) {
			hasConfirmIntentRef.current = false;
			return;
		}

		const isUnexpectedBlurOnFreshEmptyTitle =
			!hasConfirmIntentRef.current && value === '' && currentValue.trim() === '';

		if (isUnexpectedBlurOnFreshEmptyTitle) {
			requestAnimationFrame(() => textRef.current?.focus());
			return;
		}

		hasConfirmIntentRef.current = false;
		onTriggerConfirm();
	}, [currentValue, onTriggerConfirm, value]);

	const onOutsideClickWithFocus = useCallback(() => {
		hasConfirmIntentRef.current = true;
		onTriggerConfirm();
	}, [onTriggerConfirm]);

	const onBlur = fg('pol-23465-section-id-focus-fix') ? onBlurWithFocus : onTriggerConfirm;
	const onOutsideClick = fg('pol-23465-section-id-focus-fix')
		? onOutsideClickWithFocus
		: onTriggerConfirm;

	return (
		<OutsideClickAlerter onClickOutside={onOutsideClick}>
			{(outsideClickAlerterProps) => (
				<div {...outsideClickAlerterProps}>
					<AkTextField
						ref={textRef}
						aria-label={label}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.editable-title.ak-text-field"
						value={currentValue}
						onChange={onChange}
						onKeyDown={onKeyDown}
						maxLength={VIEW_NAME_MAX_LENGTH}
						onBlur={onBlur}
						css={textFieldStyles}
						elemBeforeInput={<Box xcss={elementBeforeStyles}>{elemBefore}</Box>}
					/>
				</div>
			)}
		</OutsideClickAlerter>
	);
};

const elementBeforeStyles = xcss({
	gridColumn: '1',
	display: 'flex',
	flexShrink: 0,
	width: '24px',
	height: '24px',
	alignItems: 'center',
	justifyContent: 'center',
	paddingInlineStart: 'space.075',
});

const textFieldStyles = css({
	// container styles
	display: 'grid',
	gridTemplateColumns: 'auto 1fr',
	alignItems: 'center',
	boxSizing: 'border-box',
	height: '32px',
	// input styles
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > [data-ds--text-field--input]': {
			color: token('color.text.subtle'),
			fontWeight: token('font.weight.medium'),
			gridColumn: '2',
			paddingTop: token('space.050'),
			paddingRight: token('space.075'),
			paddingBottom: token('space.050'),
			paddingLeft: token('space.075'),
			borderLeft: `${token('border.width')} solid transparent`,
		},
	},
});
